import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Globe, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

interface AllowedDomain {
  id: string;
  domain: string;
  created_at: string;
  created_by: string | null;
}

export function AllowedDomainsTab() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [newDomain, setNewDomain] = useState("");
  const [deletingDomain, setDeletingDomain] = useState<{ id: string; domain: string } | null>(null);

  const { data: domains, isLoading } = useQuery({
    queryKey: ["allowed-domains"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("allowed_domains")
        .select("*")
        .order("domain");
      if (error) throw error;
      return data as AllowedDomain[];
    },
  });

  const addDomainMutation = useMutation({
    mutationFn: async (domain: string) => {
      const { error } = await supabase
        .from("allowed_domains")
        .insert({ domain: domain.toLowerCase().trim(), created_by: user?.id });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allowed-domains"] });
      toast.success("Domain added successfully");
      setNewDomain("");
    },
    onError: (error: Error) => {
      if (error.message.includes("duplicate")) {
        toast.error("Domain already exists");
      } else {
        toast.error(error.message);
      }
    },
  });

  const deleteDomainMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("allowed_domains")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allowed-domains"] });
      toast.success("Domain removed");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const handleAddDomain = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain.trim()) return;
    
    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(newDomain.trim())) {
      toast.error("Please enter a valid domain (e.g., company.com)");
      return;
    }
    
    addDomainMutation.mutate(newDomain);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Allowed Email Domains
          </CardTitle>
          <CardDescription>
            Users with email addresses from these domains can access the app after signing in with Google.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleAddDomain} className="flex gap-2">
            <Input
              placeholder="e.g., yourcompany.com"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              className="max-w-xs"
            />
            <Button type="submit" disabled={addDomainMutation.isPending}>
              <Plus className="h-4 w-4 mr-2" />
              Add Domain
            </Button>
          </form>

          {isLoading ? (
            <div className="text-muted-foreground py-4">Loading domains...</div>
          ) : domains && domains.length > 0 ? (
            <div className="space-y-2">
              {domains.map((domain) => (
                <div
                  key={domain.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md bg-muted/50 hover:bg-muted transition-colors"
                >
                  <span className="font-medium">@{domain.domain}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeletingDomain({ id: domain.id, domain: domain.domain })}
                      disabled={deleteDomainMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground py-4">
              No domains configured. Add a domain to allow users with that email domain to access the app.
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deletingDomain} onOpenChange={(open) => !open && setDeletingDomain(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Allowed Domain</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>@{deletingDomain?.domain}</strong>? Users with this email domain will no longer be able to sign in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deletingDomain) {
                  deleteDomainMutation.mutate(deletingDomain.id);
                  setDeletingDomain(null);
                }
              }}
            >
              Remove Domain
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
